import { registerComponent, asyncRequest } from "helpers";
import { WYATTElement } from "wyatt";

registerComponent(
  "adoption-search-form",
  class AdoptionSearchForm extends WYATTElement {
    constructor() {
      super();

      this.formElement = this.querySelector("form");
    }

    // GETTERS

    get adoptionSearchCoursesInstance() {
      return this.closest(".adoption-search").querySelector(
        "adoption-search-courses"
      );
    }

    get selectElementsByName() {
      return {
        schoolId: this.querySelector("select[name='schoolId']"),
        termId: this.querySelector("select[name='termId']"),
        departmentIds: this.querySelector("select[name='departmentIds']"),
      };
    }

    get multipleSelectElementsByName() {
      return {
        schoolId: this.selectElementsByName.schoolId.closest("multiple-select"),
        termId: this.selectElementsByName.termId.closest("multiple-select"),
        departmentIds:
          this.selectElementsByName.departmentIds.closest("multiple-select"),
      };
    }

    get multipleSelectInstancesByName() {
      return {
        schoolId: this.multipleSelectElementsByName.schoolId.instance,
        termId: this.multipleSelectElementsByName.termId.instance,
        departmentIds: this.multipleSelectElementsByName.departmentIds.instance,
      };
    }

    get currentSearchParams() {
      return new URLSearchParams(window.location.search);
    }

    // From: https://github.com/ghiscoding/multiple-select-vanilla/blob/v4.3.7/packages/demo/src/examples/example07.ts#L26-L34
    get serializedFormData() {
      const result = [];

      Object.values(this.selectElementsByName).forEach((selectElement) => {
        const selectedOptions = Array.from(selectElement.selectedOptions);

        selectedOptions.forEach(({ value = "" }) => {
          if (value === "") {
            return;
          }

          result.push(`${selectElement.name}=${value}`);
        });
      });

      return result.length > 0 ? result.join("&") : "";
    }

    get isFormValid() {
      return (
        this.currentSearchParams.get("schoolId") &&
        this.currentSearchParams.get("termId") &&
        this.currentSearchParams.getAll("departmentIds").length > 0
      );
    }

    // EVENTS

    async connectedCallback() {
      this.bindEvents();
      this.loadSchoolSelectOptions();

      if (this.isFormValid) {
        this.toggleSubmitButton();
        this.loadAdoptionSearchResults();
      }
    }

    bindEvents() {
      this.formElement.addEventListener(
        "change",
        this.handleFormChange.bind(this)
      );

      this.formElement.addEventListener(
        "submit",
        this.handleFormSubmit.bind(this)
      );
    }

    // EVENT HANDLERS

    handleSchoolSelectChange(event) {
      const { value: schoolId = "" } = event?.item ?? {};

      if (schoolId === "") {
        this.multipleSelectInstancesByName.termId.setSelects([]);
        this.multipleSelectInstancesByName.termId.disable();
        this.multipleSelectInstancesByName.departmentIds.setSelects([]);
        this.multipleSelectInstancesByName.departmentIds.disable();
        return;
      }

      this.loadTermSelectOptions(schoolId);
    }

    handleTermSelectChange(event) {
      const { value: termId = "" } = event?.item ?? {};

      if (termId === "") {
        this.multipleSelectInstancesByName.departmentIds.setSelects([]);
        this.multipleSelectInstancesByName.departmentIds.disable();
        return;
      }

      this.loadDepartmentSelectOptions(termId);
    }

    handleFormChange() {
      const url = new URL(window.location.href);

      url.search = this.serializedFormData;

      window.history.replaceState({}, "", url);

      this.toggleSubmitButton();
    }

    handleFormSubmit(event) {
      event.preventDefault();

      this.loadAdoptionSearchResults();
    }

    // DOM HELPERS

    toggleSubmitButton() {
      this.formElement.querySelector("button[type='submit']").disabled =
        !this.isFormValid;
    }

    removeOptionsFromSelectElement(selectElement) {
      selectElement.innerHTML = "";
    }

    addOptionElementToSelectElement(selectElement, option = {}) {
      const { text, value = "", selected = false } = option;

      const optionElement = document.createElement("option");

      optionElement.value = value;
      optionElement.textContent = text;
      optionElement.selected = selected;

      selectElement.appendChild(optionElement);
    }

    // API HELPERS

    fetchEndpoint(url, params) {
      let endpoint = url;

      if (params) {
        endpoint += `?${new URLSearchParams(params).toString()}`;
      }

      return asyncRequest(
        endpoint,
        {},
        {
          responseFormat: "json",
        }
      );
    }

    async loadSchoolSelectOptions() {
      const schoolIdParam = this.currentSearchParams.get("schoolId");

      const response = await this.fetchEndpoint(this.config.endpoints.schools);

      if (!response?.items) {
        throw new Error("No schools found");
      }

      const options = response.items.map((item) => ({
        text: item.school_name,
        value: item.school_id,
      }));

      this.removeOptionsFromSelectElement(this.selectElementsByName.schoolId);

      this.addOptionElementToSelectElement(this.selectElementsByName.schoolId, {
        text: WYATT.t.adoptionSearch.makeSelection,
        selected: true,
      });

      options.forEach((option) => {
        this.addOptionElementToSelectElement(
          this.selectElementsByName.schoolId,
          option
        );
      });

      this.multipleSelectElementsByName.schoolId.reinitialize();

      this.multipleSelectInstancesByName.schoolId.refreshOptions({
        onChange: this.handleSchoolSelectChange.bind(this),
      });

      this.multipleSelectInstancesByName.schoolId.enable();

      if (schoolIdParam) {
        this.multipleSelectInstancesByName.schoolId.setSelects([schoolIdParam]);
        this.handleSchoolSelectChange({ item: { value: schoolIdParam } });
      }
    }

    async loadTermSelectOptions(schoolId) {
      const termIdParam = this.currentSearchParams.get("termId");

      const response = await this.fetchEndpoint(this.config.endpoints.terms, {
        schoolId,
      });

      if (!response?.items) {
        throw new Error("No terms found");
      }

      const options = response.items.map((item) => ({
        text: item.term_name,
        value: item.term_id,
      }));

      this.removeOptionsFromSelectElement(this.selectElementsByName.termId);

      this.addOptionElementToSelectElement(this.selectElementsByName.termId, {
        text: WYATT.t.adoptionSearch.makeSelection,
        selected: true,
      });

      options.forEach((option) => {
        this.addOptionElementToSelectElement(
          this.selectElementsByName.termId,
          option
        );
      });

      this.multipleSelectElementsByName.termId.reinitialize();

      this.multipleSelectInstancesByName.termId.refreshOptions({
        onChange: this.handleTermSelectChange.bind(this),
      });

      this.multipleSelectInstancesByName.termId.enable();

      if (termIdParam) {
        this.multipleSelectInstancesByName.termId.setSelects([termIdParam]);
        this.handleTermSelectChange({ item: { value: termIdParam } });
      }
    }

    async loadDepartmentSelectOptions(termId) {
      const schoolIdParam = this.currentSearchParams.get("schoolId");
      const departmentIdsParam =
        this.currentSearchParams.getAll("departmentIds");

      const response = await this.fetchEndpoint(
        this.config.endpoints.departments,
        {
          termId,
          schoolId: schoolIdParam,
        }
      );

      if (!response?.items) {
        throw new Error("No departments found");
      }

      const options = response.items.map((item) => ({
        text: `${item.department_code} - ${item.department_name}`,
        value: item.department_id,
      }));

      this.removeOptionsFromSelectElement(
        this.selectElementsByName.departmentIds
      );

      options.forEach((option) => {
        this.addOptionElementToSelectElement(
          this.selectElementsByName.departmentIds,
          option
        );
      });

      this.multipleSelectElementsByName.departmentIds.reinitialize();
      this.multipleSelectInstancesByName.departmentIds.enable();

      if (departmentIdsParam) {
        this.multipleSelectInstancesByName.departmentIds.setSelects(
          departmentIdsParam
        );
      }
    }

    async loadAdoptionSearchResults() {
      const schoolId = this.currentSearchParams.get("schoolId");
      const termId = this.currentSearchParams.get("termId");
      const departmentIds = this.currentSearchParams.getAll("departmentIds");

      this.adoptionSearchCoursesInstance.addLoading();

      const response = await this.fetchEndpoint(this.formElement.action, {
        schoolId,
        termId,
        departmentIds,
      });

      if (!response.items) {
        throw new Error("No courses found");
      }

      this.adoptionSearchCoursesInstance.removeLoading();

      this.publish(
        WYATT.config.pubsub.events.adoptionSearchFormSubmit,
        response
      );
    }
  }
);
